<template>
  <div v-if="isVisible" class="custom-modal-wrapper">
    <div class="custom-modal-backdrop" @click="hide"></div>
    <div class="custom-modal">
      <div class="custom-modal-header">
        <h5 class="custom-modal-title">Neue Frage hinzufügen</h5>
        <button type="button" class="custom-modal-close" @click="hide">×</button>
      </div>
      <div class="custom-modal-body">
        <div class="question-form">
          <form @submit.prevent="saveQuestion">
            <div class="form-group mb-3">
              <label for="questionType" class="form-label fw-bold">Fragetyp</label>
              <select 
                id="questionType" 
                v-model="defaultQuestion.type" 
                class="form-select"
                required
              >
                <option value="multiple_choice" selected>Multiple-Choice (Auswahl)</option>
                <option value="open">Offene Frage</option>
              </select>
            </div>
            
            <!-- Fragengruppen-Auswahl -->
            <div class="form-group mb-3">
              <label for="questionGroup" class="form-label fw-bold">
                <span class="icon icon-primary me-2"><i class="fas fa-layer-group"></i></span>
                Fragengruppe
              </label>
              <select 
                id="questionGroup" 
                v-model="defaultQuestion.question_group_id" 
                class="form-select"
              >
                <option value="">Keine Gruppe</option>
                <option 
                  v-for="group in questionGroups" 
                  :key="group.id" 
                  :value="group.id"
                >
                  {{ group.name }} ({{ group.questions_count }} Fragen)
                </option>
              </select>
              <small class="form-text text-muted">
                Optional: Ordnen Sie diese Frage einer thematischen Gruppe zu
              </small>
            </div>
            
            <!-- Manuelle Reihung -->
            <div class="form-group mb-3">
              <label for="questionSortOrder" class="form-label fw-bold">
                <span class="icon icon-primary me-2"><i class="fas fa-sort-numeric-up"></i></span>
                Reihung
              </label>
              <input 
                type="number" 
                id="questionSortOrder" 
                v-model.number="defaultQuestion.sort_order" 
                class="form-control"
                min="1"
                placeholder="Position in der Fragenreihe (optional)"
                @input="checkSortOrderConflict"
              />
              <small class="form-text text-muted">
                Leer lassen für automatische Positionierung am Ende
              </small>
              
              <!-- Konfliktwarnung für Reihung -->
              <div v-if="sortOrderConflict" class="alert alert-warning mt-2">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <strong>Achtung:</strong> Diese Position ist bereits vergeben. Die bestehende Frage wird nach unten verschoben.
              </div>
            </div>
            
            <!-- Auswahlmöglichkeit für Single- oder Multiple-Choice -->
            <div class="form-group mb-3" v-if="defaultQuestion.type === 'multiple_choice'">
              <label class="form-label fw-bold">Antwortmöglichkeiten</label>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  id="singleAnswer" 
                  name="answerType" 
                  :value="false" 
                  v-model="defaultQuestion.allow_multiple_answers"
                >
                <label class="form-check-label" for="singleAnswer">
                  Eine Antwort möglich (Single-Choice)
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  id="multipleAnswers" 
                  name="answerType" 
                  :value="true" 
                  v-model="defaultQuestion.allow_multiple_answers"
                >
                <label class="form-check-label" for="multipleAnswers">
                  Mehrere Antworten möglich (Multiple-Choice)
                </label>
              </div>
            </div>
            
            <div class="mb-3">
              <label for="questionDescription" class="form-label">Fragebeschreibung</label>
              <textarea 
                class="form-control" 
                id="questionDescription" 
                v-model="defaultQuestion.description" 
                rows="3"
                placeholder="Geben Sie Ihre Frage ein..."
                required
                @input="checkForDuplicates"
              ></textarea>
              
              <!-- Duplikats-Warnung -->
              <div v-if="duplicateWarning" class="alert alert-warning mt-2">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <strong>Achtung:</strong> Eine ähnliche Frage existiert bereits:
                <div class="mt-2">
                  <strong>{{ duplicateWarning.existing_question.description }}</strong>
                  <br>
                  <small class="text-muted">Frage-ID: {{ duplicateWarning.existing_question.id }}</small>
                </div>
              </div>
            </div>

            <!-- Bildupload für Fragen -->
            <div class="form-group mb-3">
              <label for="questionImage" class="form-label fw-bold">Bild (optional)</label>
              <div class="custom-file mb-2">
                <input 
                  type="file" 
                  class="form-control" 
                  id="questionImage" 
                  @change="handleQuestionImageUpload"
                  accept="image/*"
                />
              </div>
              
              <!-- Bildvorschau, falls vorhanden -->
              <div v-if="defaultQuestion.image_url || questionImagePreview" class="mt-2">
                <div class="question-image-preview">
                  <img 
                    :src="questionImagePreview || defaultQuestion.image_url" 
                    alt="Bildvorschau" 
                    class="img-thumbnail"
                    style="max-height: 200px;"
                  />
                </div>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-danger mt-2" 
                  @click="removeQuestionImage"
                >
                  <i class="fas fa-trash-alt me-1"></i> Bild entfernen
                </button>
              </div>
            </div>
            
            <!-- Hinweis zu Antworten -->
            <div v-if="defaultQuestion.type === 'multiple_choice'" class="alert alert-info mb-3">
              <i class="fas fa-info-circle me-2"></i>
              <strong>Hinweis:</strong> Mindestens 2 Antworten sind erforderlich. Die weiteren Antworten sind optional. 
              Mindestens eine Antwort muss als richtig markiert werden.
            </div>
            
            <div v-else-if="defaultQuestion.type === 'open'" class="alert alert-warning mb-3">
              <i class="fas fa-exclamation-triangle me-2"></i>
              <strong>Hinweis:</strong> Punktevergabe erfolgt manuell durch die Prüfer:innen.
            </div>
            
            <!-- Antworten für Multiple-Choice-Fragen -->
            <div v-if="defaultQuestion.type === 'multiple_choice'" class="answers-container">
              <h5 class="mb-3">Antwortmöglichkeiten</h5>
              
              <div v-for="i in 6" :key="`answer-${i}`" class="answer-item mb-3">
                <div class="input-group">
                  <!-- Single-Choice: Radio-Buttons -->
                  <div v-if="!defaultQuestion.allow_multiple_answers" class="input-group-text">
                    <input 
                      type="radio" 
                      :id="`is_answer_${i}_correct`" 
                      v-model="singleCorrectAnswer" 
                      :value="i" 
                      class="form-check-input"
                      @change="setSingleCorrectAnswer(i)"
                    >
                  </div>
                  
                  <!-- Multiple-Choice: Checkboxen -->
                  <div v-else class="input-group-text">
                    <input 
                      type="checkbox" 
                      :id="`is_answer_${i}_correct`" 
                      v-model="defaultQuestion[`is_answer_${i}_correct`]" 
                      class="form-check-input"
                    >
                  </div>
                  
                  <input 
                    type="text" 
                    class="form-control" 
                    :placeholder="`Antwort ${i}`" 
                    v-model="defaultQuestion[`answer_${i}`]"
                  >
                </div>
              </div>
            </div>
            
            <!-- Punkte nur für Multiple-Choice-Fragen -->
            <div v-if="defaultQuestion.type === 'multiple_choice'" class="form-group mb-3">
              <label for="questionPoints" class="form-label fw-bold">Punkte</label>
              <input 
                type="number" 
                id="questionPoints" 
                v-model.number="defaultQuestion.points" 
                class="form-control"
                min="0"
                :placeholder="defaultPointsPerQuestion + ' (Standard)'"
              />
              <small class="form-text text-muted">
                Leer lassen für Standardpunkte ({{ defaultPointsPerQuestion }} Punkte)
              </small>
            </div>
            
            <div class="d-flex justify-content-between mt-4">
              <button 
                type="button" 
                class="btn btn-outline-secondary" 
                @click="hide"
              >
                Abbrechen
              </button>
              <div class="d-flex gap-2">
                <button 
                  type="button" 
                  class="btn btn-info"
                  :disabled="loading"
                  @click="saveAndAddNextQuestion"
                >
                  <i class="fas fa-plus-circle me-2"></i>
                  Nächste Frage
                </button>
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="loading"
                >
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="fas fa-check me-2"></i>
                  Fertig
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useQuestionGroupsStore } from '@/stores/questionGroups';

export default {
  name: 'CreateQuestionModal',
  emits: [
    'save',
    'save-and-add-next',
    'reset',
    'image-updated',
    'image-removed'
  ],
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    defaultPointsPerQuestion: {
      type: Number,
      default: 10
    },
    questionType: {
      type: String,
      default: 'multiple_choice'
    }
  },
  setup() {
    const questionGroupsStore = useQuestionGroupsStore();
    return { questionGroupsStore };
  },
  data() {
    return {
      isVisible: false,
      defaultQuestion: this.getDefaultQuestion(),
      questionImagePreview: null,
      questionImageFile: null,
      singleCorrectAnswer: 0,
      questionGroups: [],
      duplicateWarning: null,
      sortOrderConflict: false
    };
  },
  computed: {
    questionGroups() {
      return this.questionGroupsStore.getAllQuestionGroups;
    }
  },
  watch: {
    questionType(newType) {
      this.defaultQuestion.type = newType;
      // Punkte anpassen basierend auf Fragetyp
      if (newType === 'open') {
        this.defaultQuestion.points = 0;
        this.defaultQuestion.max_points = 0;
      } else if (newType === 'multiple_choice') {
        this.defaultQuestion.points = this.defaultPointsPerQuestion;
      }
    }
  },
  methods: {
    getDefaultQuestion() {
      return {
        id: null,
        type: this.questionType,
        description: '',
        points: this.questionType === 'multiple_choice' ? this.defaultPointsPerQuestion : 0,
        max_points: null,
        image_url: null,
        question_group_id: null,
        sort_order: null,
        allow_multiple_answers: false, // Default ist "Eine Antwort möglich"
        answer_1: '',
        answer_2: '',
        answer_3: '',
        answer_4: '',
        answer_5: '',
        answer_6: '',
        is_answer_1_correct: false,
        is_answer_2_correct: false,
        is_answer_3_correct: false,
        is_answer_4_correct: false,
        is_answer_5_correct: false,
        is_answer_6_correct: false
      };
    },
    saveQuestion() {
      // Validierung und Aufbereitung der Daten
      const questionToSave = this.prepareQuestionForSave();
      if (questionToSave) {
        this.$emit('save', questionToSave);
      }
    },
    saveAndAddNextQuestion() {
      const questionToSave = this.prepareQuestionForSave();
      if (questionToSave) {
        this.$emit('save-and-add-next', questionToSave);
        // Formular zurücksetzen für die nächste Frage
        this.resetForm();
      }
    },
    resetForm() {
      this.defaultQuestion = this.getDefaultQuestion();
      this.questionImagePreview = null;
      this.questionImageFile = null;
      this.singleCorrectAnswer = 0;
      this.duplicateWarning = null; // Reset Duplikat-Warnung
      this.sortOrderConflict = false; // Reset Konflikt-Warnung
      
      // File-Input-Feld zurücksetzen
      this.$nextTick(() => {
        const fileInput = document.getElementById('questionImage');
        if (fileInput) {
          fileInput.value = '';
        }
      });
      
      this.$emit('reset');
    },
    show() {
      console.log('CreateQuestionModal: Zeige Modal an');
      document.body.style.overflow = 'hidden'; // Body-Scrolling deaktivieren
      this.isVisible = true;
      console.log('CreateQuestionModal: isVisible ist jetzt', this.isVisible);
    },
    hide() {
      console.log('CreateQuestionModal: Verstecke Modal');
      document.body.style.overflow = ''; // Body-Scrolling wiederherstellen
      this.isVisible = false;
      this.resetForm();
    },
    handleQuestionImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!file.type.match('image.*')) {
        this.$toast.error('Bitte laden Sie ein gültiges Bild hoch.');
        return;
      }
      
      this.questionImageFile = file;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.questionImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      this.$emit('image-updated', file);
    },
    removeQuestionImage() {
      this.questionImageFile = null;
      this.questionImagePreview = null;
      this.defaultQuestion.image_url = null;
      
      this.$emit('image-removed');
    },
    setSingleCorrectAnswer(index) {
      // Alle Antworten auf falsch setzen
      for (let i = 1; i <= 6; i++) {
        this.defaultQuestion[`is_answer_${i}_correct`] = false;
      }
      // Nur die ausgewählte Antwort auf wahr setzen
      this.defaultQuestion[`is_answer_${index}_correct`] = true;
    },
    initializeSingleCorrectAnswer() {
      if (!this.defaultQuestion.allow_multiple_answers) {
        // Finde die erste korrekte Antwort
        for (let i = 1; i <= 6; i++) {
          if (this.defaultQuestion[`is_answer_${i}_correct`]) {
            this.singleCorrectAnswer = i;
            return;
          }
        }
        this.singleCorrectAnswer = 0;
      }
    },
    checkForDuplicates() {
      if (this.defaultQuestion.description && this.defaultQuestion.description.trim() !== '') {
        this.$emit('check-for-duplicates', this.defaultQuestion.description);
      } else {
        this.duplicateWarning = null;
      }
    },
    handleDuplicateWarning(warning) {
      this.duplicateWarning = warning;
    },
    checkSortOrderConflict() {
      if (this.defaultQuestion.sort_order) {
        this.$emit('check-sort-order-conflict', this.defaultQuestion.sort_order);
      }
    },
    handleSortOrderConflict(conflict) {
      this.sortOrderConflict = conflict;
    },
    prepareQuestionForSave() {
      // Kopie der Frage erstellen
      const question = { ...this.defaultQuestion };
      
      // Sicherstellen, dass die Beschreibung ausgefüllt ist
      if (!question.description || question.description.trim() === '') {
        this.$toast.error('Bitte geben Sie eine Fragebeschreibung ein');
        return null;
      }
      
      // Sicherstellen, dass der Typ korrekt ist
      if (question.type !== 'multiple_choice' && question.type !== 'open') {
        question.type = 'multiple_choice';
      }
      
      // Punkte validieren (nur für Multiple-Choice-Fragen)
      if (question.type === 'multiple_choice') {
        if (!question.points || question.points < 1) {
          question.points = this.defaultPointsPerQuestion; // Standardwert
        }
      } else if (question.type === 'open') {
        // Offene Fragen haben keine Punkte
        question.points = 0;
        question.max_points = 0;
      }
      
      // Sortierreihenfolge validieren
      if (question.sort_order && question.sort_order < 1) {
        this.$toast.error('Die Reihung muss mindestens 1 sein');
        return null;
      }
      
      // Bei Multiple-Choice-Fragen sicherstellen, dass mindestens eine Antwort korrekt ist
      if (question.type === 'multiple_choice') {
        const hasCorrectAnswer = 
          question.is_answer_1_correct || 
          question.is_answer_2_correct || 
          question.is_answer_3_correct || 
          question.is_answer_4_correct || 
          question.is_answer_5_correct || 
          question.is_answer_6_correct;
        
        if (!hasCorrectAnswer) {
          this.$toast.error('Bitte markieren Sie mindestens eine korrekte Antwort');
          return null;
        }
        
        // Bei Single-Choice (allow_multiple_answers = false) sicherstellen, dass nur eine Antwort korrekt ist
        if (!question.allow_multiple_answers) {
          const correctAnswersCount = 
            (question.is_answer_1_correct ? 1 : 0) + 
            (question.is_answer_2_correct ? 1 : 0) + 
            (question.is_answer_3_correct ? 1 : 0) + 
            (question.is_answer_4_correct ? 1 : 0) + 
            (question.is_answer_5_correct ? 1 : 0) + 
            (question.is_answer_6_correct ? 1 : 0);
          
          if (correctAnswersCount > 1) {
            this.$toast.error('Bei Einfachauswahl (Single-Choice) darf nur eine Antwort korrekt sein');
            return null;
          }
        }
      }
      
      return question;
    }
  },
  created() {
    this.initializeSingleCorrectAnswer();
  }
};
</script> 

<style lang="scss" scoped>
.custom-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.custom-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

.custom-modal {
  position: relative;
  background-color: #fff;
  width: 90%;
  max-width: 800px;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: visible;
}

.custom-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #4776E6, #8E54E9);
  color: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.custom-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.custom-modal-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  margin: 0;
}

.custom-modal-body {
  padding: 1.5rem;
  overflow: visible;
}

/* Dropdown-Fixes: verhindert Überdeckung/Abschneiden */
.form-group {
  position: relative;
}

.form-select {
  position: relative;
  z-index: 2;
}

/* Zusätzlicher Raum unterhalb des Selects, damit die geöffnete Liste nicht überlappt */
#questionGroup {
  margin-bottom: 0.5rem;
}

/* Stacked context: Options-Liste über benachbarten Elementen */
:deep(.form-select:focus) {
  z-index: 3;
}

/* Falls ein UI-Framework die Liste als Popup rendert, heben wir sie an */
:deep(.dropdown-menu),
:deep(.select-dropdown),
:deep(.v-select .vs__dropdown-menu) {
  z-index: 10000;
}

.question-form {
  .form-group {
    margin-bottom: 1.25rem;
  }
  
  .question-image-preview {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.08));
    display: inline-block;
    margin-bottom: 0.75rem;
    
    img {
      max-width: 100%;
      border-radius: 8px;
    }
  }
}

.answer-item {
  position: relative;
  
  .input-group {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    
    .input-group-text {
      background-color: #f8f9fa;
      border-right: none;
    }
    
    .form-control {
      border-left: none;
      border-radius: 0 8px 8px 0;
    }
    
    .btn {
      border-radius: 0 8px 8px 0;
    }
  }
}
</style> 